/**
 * Words and numbers for processing progress (T-0159).
 *
 * Every percentage here is `done / total` of a real count the server
 * reported (pages read, passages stored, sections analysed), rounded DOWN so
 * nothing reads 100% before it is done. When the server does not know the
 * amount of work yet, there is no percentage at all: "Reading…".
 */

export interface ProgressCount {
  done: number;
  total: number;
}

/** Whole percent of `done` over `total`, rounded down; null when there is nothing to count. */
export function progressPercent(p: ProgressCount | null | undefined): number | null {
  if (!p || !Number.isFinite(p.total) || p.total <= 0) return null;
  const ratio = Math.min(Math.max(p.done, 0), p.total) / p.total;
  return Math.floor(ratio * 100);
}

export function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** "52 of 128 pages", "1 of 1 page". */
export function countPhrase(p: ProgressCount, singular: string, plural = `${singular}s`): string {
  return `${formatCount(p.done)} of ${formatCount(p.total)} ${p.total === 1 ? singular : plural}`;
}

/**
 * "Reading 40% · 52 of 128 pages", or "Reading…" when the amount of work is
 * not known.
 */
export function progressLabel(
  verb: string,
  p: ProgressCount | null | undefined,
  singular: string,
  plural?: string,
): string {
  const percent = progressPercent(p);
  if (percent === null || !p) return `${verb}…`;
  return `${verb} ${percent}% · ${countPhrase(p, singular, plural)}`;
}
